/**
 * Support module for Mock.
 * This file provides shared implementation details for its surrounding feature or platform area.
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  AgentApprovalRequestRecord,
  AgencyRecord,
  AgencyUpsert,
  AgentRecord,
  ApiRepository,
  DashboardActiveSummary,
  DashboardPdfExport,
  DashboardSummary,
  InventoryCatalogExcelExport,
  UploadedFileResult,
  SupervisorDashboardSummary,
  ApiUserSummary,
  AuthCredentials,
  AuthUser,
  CreateUserPayload,
  PagedResult,
  PosListQuery,
  PosRecord,
  PosStockEntry,
  PosStockDetails,
  PosUpsertPayload,
  ProspectListQuery,
  ProspectApprovalPayload,
  ProspectRecord,
  ProspectUpsertPayload,
  SupervisorSignupRequestPayload,
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
  TaskRecord,
  AgentTargetAnalyticsResult,
  AgentTargetRecord,
  UpdatePreferencesPayload,
  UpdateProfilePayload,
  UpdateUserProfilePayload,
  UpdateUserStatusPayload,
  UsersListParams,
  UsersListResult,
  AgentListQuery,
  AgentInventoryTransactionPayload,
  AgencyInventoryTransactionPayload,
  CashBalance,
  CashBalanceListQuery,
  CashMovement,
  CashMovementListQuery,
  CashMovementPayload,
  CashOwnerType,
  UpdateAgencyStockVariantPayload,
  UpdatePosStockVariantPayload,
  UpdateProspectStockVariantPayload,
  UpdateAgentStockVariantPayload,
  AgencyDirectSalesLogQuery,
  AgencyDirectSalesLogRecord,
  AgencyInventoryStockCategoryGroup,
  AgencyListQuery,
  TaskListQuery,
  TaskActivityListQuery,
  CreateTaskPayload,
  UpdateTaskPayload,
  AgentInventoryStockCategoryGroup,
  AgentInventoryTransactionRecord,
  AgencyInventoryTransactionRecord,
  CentralBankingNotePayload,
  CentralBankingNoteRow,
  CentralTransferListRow,
  CentralStockListRow,
  CentralStockOverviewData,
  CreateStockThresholdPayload,
  InventoryCategory,
  InventoryProduct,
  InventoryVariant,
  CreateCategoryPayload,
  CentralAddStockPayload,
  AgencyReferenceOption,
  CreateProductPayload,
  CreateVariantPayload,
  MaintenanceTypeRecord,
  BtlCampaignApiRecord,
  BtlCampaignListItem,
  BtlCampaignListQuery,
  BtlCampaignUpsertPayload,
  BtlDashboardSummary,
  BtlUserApiRecord,
  BtlUserUpdatePayload,
  CentralReference,
  LogisticsDashboardSummary,
  LogisticsTaskDetailApiRecord,
  LogisticsTaskListItem,
  LogisticsTaskListQuery,
  LogisticsUserApiRecord,
  LogisticsTaskUpsertPayload,
  StockThresholdQuery,
  StockThresholdRow,
  VehicleApiRecord,
  CreateVehiclePayload,
  UpdateVehiclePayload,
  CreateVehicleDocumentStandalonePayload,
  UpdateVehicleDocumentPayload,
  VehicleDocumentApiRecord,
  VehicleDocumentListQuery,
  FuelRequestApiRecord,
  FuelRequestListQuery,
  CreateFuelRequestPayload,
  UpdateFuelRequestPayload,
  MaintenanceRequestApiRecord,
  MaintenanceRequestListQuery,
  CreateMaintenanceRequestPayload,
  UpdateMaintenanceRequestPayload,
  maintenanceActionPayload,
  fuelActionPayload,
  LogisticsTaskReviewPayload,
  LogisticsTaskReviewResult,
  AgentTaskReviewPayload,
  AgentTaskReviewResult,
  AgentTargetPayload,
  TargetListQuery,
  TargetPerformanceAnalyticsDocumentExport,
  TargetPerformanceAnalyticsDocumentQuery,
  TargetPerformanceAnalyticsQuery,
  TaskActivity
} from './api-repository';
import { AgencyProfileConfiguration, toAgencyRecord } from '../../../features/agencies/agencies.types';
import { DEFAULT_AGENT_PROFILE, toAgentRecord } from '../../../features/agents/agents.types';
import { PosStockTransactionPayload, ProspectStockTransactionPayload, toPosRecord, toProspectRecord } from '../../../features/pos/pos.types';
import { toSupervisorRecord } from '../../../features/supervisors/supervisors.types';
import { buildReviewRows, cycleValue, formatDisplayReviewDate, formatIsoReviewDate, formatSequence } from '../../../shared/utils';

interface AuthMockFile {
  defaultOtp: string;
  users: Array<AuthUser & { password: string }>;
}

interface SupervisorsMockFile {
  approved: SupervisorRecord[];
  pending: SupervisorApprovalRequestRecord[];
}

/**
 * SERVICE: Mock Repository Ts
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class MockRepository extends ApiRepository {
  private readonly http = inject(HttpClient);
  private readonly sessionOtp = new Map<string, string>();
  /**
   * SIDE EFFECT:
   * The mock repository caches seeded datasets in memory after the first read so repeated screen
   * navigation behaves more like a live app session than a static fixture reload on every request.
   */
  private authCache: AuthMockFile | null = null;
  private supervisorsCache: SupervisorsMockFile | null = null;
  private agenciesCache: AgencyRecord[] | null = null;
  private agentsCache: AgentRecord[] | null = null;
  private posCache: PosRecord[] | null = null;
  private prospectCache: ProspectRecord[] | null = null;
  private pendingAgentRequestsCache: AgentApprovalRequestRecord[] | null = null;
  private tasksCache: TaskRecord[] | null = null;
  private targetsCache: AgentTargetRecord[] | null = null;
  private stockThresholdsCache: StockThresholdRow[] = [];

  override async login(payload: AuthCredentials): Promise<AuthUser | null> {
    const auth = await this.readAuthMock();
    const match = auth.users.find((item) => item.email === payload.email && item.password === payload.password);

    if (!match || match.status === 'pending') {
      return null;
    }

    return {
      authUserId: match.authUserId ?? match.email,
      email: match.email,
      role: match.role,
      name: match.name,
      firstName: match.firstName ?? match.name.split(' ')[0] ?? match.name,
      lastName: match.lastName ?? match.name.split(' ').slice(1).join(' '),
      phone: match.phone ?? '+237 677 112 233',
      address: match.address ?? 'Bonapriso, Douala, Cameroon',
      avatar: match.avatar ?? '',
      language: match.language ?? 'en',
      roles: match.roles ?? ['Admin', 'Inventory Manager', 'Finance Admin'],
      customRbacRoles: match.customRbacRoles ?? ['Agency Operations Review', 'Central Stock Approval'],
      status: match.status ?? 'active',
      userType: match.userType ?? (match.role === 'admin' ? 'Platform Admin' : 'Field Supervisor'),
      agency: match.agency ?? 'Palladium Agency Douala Central',
      supervisor: match.supervisor ?? 'Regional Director - Francophone Cluster'
    };
  }

  override logout(_sessionId: string): Promise<void> {
    return Promise.resolve();
  }

  override async updateProfile(payload: UpdateProfilePayload): Promise<AuthUser> {
    const profile = await this.getProfile();
    return {
      ...profile,
      avatar: Object.prototype.hasOwnProperty.call(payload, 'profilePhoto') ? (payload.profilePhoto ?? '') : profile.avatar
    };
  }

  override updatePreferences(_payload: UpdatePreferencesPayload): Promise<void> {
    return Promise.resolve();
  }

  override changePassword(_currentPassword: string, _newPassword: string): Promise<void> {
    return Promise.resolve();
  }

  override async getProfile(): Promise<AuthUser> {
    const auth = await this.readAuthMock();
    const user = auth.users[0];
    return {
      email: user.email,
      authUserId: user.authUserId ?? user.email,
      role: user.role,
      name: user.name,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      address: user.address,
      avatar: user.avatar ?? '',
      language: user.language ?? 'en',
      status: user.status ?? 'active',
      userType: user.userType,
      roles: user.roles,
      customRbacRoles: user.customRbacRoles
    };
  }

  override async requestSignup(payload: SupervisorSignupRequestPayload): Promise<void> {
    const auth = await this.readAuthMock();
    const supervisors = await this.readSupervisorsMock();

    const normalizedEmail = payload.email.trim().toLowerCase();
    const phoneNumber = payload.phone.replace(/\s+/g, '');
    const alreadyExists =
      auth.users.some((item) => item.email.toLowerCase() === normalizedEmail) ||
      supervisors.pending.some((item) => item.email.toLowerCase() === normalizedEmail) ||
      supervisors.approved.some((item) => item.email.toLowerCase() === normalizedEmail);

    if (alreadyExists) {
      throw new Error('A supervisor request already exists for this email.');
    }

    const nextRequestNumber = supervisors.pending.length + 1;
    const requestId = `sup-req-${String(nextRequestNumber).padStart(3, '0')}`;

    this.supervisorsCache = {
      approved: supervisors.approved,
      pending: [
        {
          id: requestId,
          name: payload.fullName.trim(),
          email: normalizedEmail,
          contact: {
            email: normalizedEmail,
            phone: {
              dialCode: 237,
              iso2: 'cm',
              country: 'Cameroon',
              number: phoneNumber
            }
          },
          requestedAgency: null,
          requestDate: new Date().toISOString(),
          approvalStatus: 'pending'
        },
        ...supervisors.pending
      ]
    };

    this.authCache = {
      ...auth,
      users: [
        ...auth.users,
        {
          authUserId: normalizedEmail,
          email: normalizedEmail,
          password: payload.password,
          role: 'supervisor',
          name: payload.fullName.trim(),
          phone: payload.phone.trim(),
          language: 'en',
          status: 'pending',
          userType: 'Field Supervisor',
          agency: '',
          supervisor: 'Awaiting admin approval'
        }
      ]
    };
  }

  override async requestPasswordOtp(email: string): Promise<boolean> {
    const auth = await this.readAuthMock();
    const hasUser = auth.users.some((item) => item.email === email);

    if (!hasUser) {
      return false;
    }

    this.sessionOtp.set(email, auth.defaultOtp);
    return true;
  }

  override verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return Promise.resolve(this.sessionOtp.get(email) === otp);
  }

  override resetPassword(email: string, _: string): Promise<boolean> {
    this.sessionOtp.delete(email);
    return Promise.resolve(Boolean(email));
  }

  override checkUserEmailExists(_email: string): Promise<{ exists: boolean; message: string }> {
    return Promise.resolve({ exists: false, message: '' });
  }

  // override async getAgencies(): Promise<AgencyRecord[]> {
  //   return this.readAgenciesMock();
  // }

  override async getAgencies(_query: AgencyListQuery = {}): Promise<PagedResult<AgencyRecord>> {
  const items = await this.readAgenciesMock();
  return { items, total: items.length, page: 1, limit: items.length };
}

  override async getAgencyProfiles(query: { search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<AgencyProfileConfiguration>> {
    const allItems: AgencyProfileConfiguration[] = [
      { id: 'point-of-connect', name: 'Point of Connect' },
      { id: 'sales-administration-office', name: 'Sales Administration Office' },
      { id: 'mini-service-container', name: 'Mini Service Container' }
    ];
    const search = query.search?.trim().toLowerCase();
    const filtered = search ? allItems.filter((item) => item.name.toLowerCase().includes(search)) : allItems;
    const page = query.page ?? 1;
    const limit = query.limit ?? 20;
    const start = (page - 1) * limit;

    return {
      items: filtered.slice(start, start + limit),
      total: filtered.length,
      page,
      limit
    };
  }

  override async getAgencyProfileById(id: string): Promise<AgencyProfileConfiguration | null> {
    const result = await this.getAgencyProfiles({ page: 1, limit: 100 });
    return result.items.find((item) => item.id === id) ?? null;
  }

  override async createAgencyProfile(payload: { name: string }): Promise<AgencyProfileConfiguration> {
    const now = new Date().toISOString();
    return {
      id: `agency-profile-${Math.random().toString(16).slice(2, 6)}`,
      name: payload.name,
      createdAt: now,
      updatedAt: now
    };
  }

  override async updateAgencyProfile(id: string, payload: { name: string }): Promise<AgencyProfileConfiguration> {
    return {
      id,
      name: payload.name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  override async deleteAgencyProfile(_id: string): Promise<void> {}

  override async getMyAgency(): Promise<AgencyRecord | null> {
    const agencies = await this.readAgenciesMock();
    return agencies[0] ?? null;
  }

  override async getAgencyById(id: string): Promise<AgencyRecord | null> {
    const agencies = await this.readAgenciesMock();
    return agencies.find((item) => item.id === id) ?? null;
  }

  override async createAgency(payload: AgencyUpsert): Promise<AgencyRecord> {
    const agencies = await this.readAgenciesMock();
    const created = toAgencyRecord(`ag-${Math.random().toString(16).slice(2, 6)}`, payload);
    this.agenciesCache = [created, ...agencies];
    return created;
  }

  override async updateAgency(id: string, payload: AgencyUpsert): Promise<AgencyRecord | null> {
    const current = await this.getAgencyById(id);

    if (!current) {
      return null;
    }

    const updated = toAgencyRecord(id, payload, current);
    const agencies = await this.readAgenciesMock();
    this.agenciesCache = agencies.map((item) => (item.id === id ? updated : item));
    return updated;
  }

  override async getAgents(query: AgentListQuery = {}): Promise<PagedResult<AgentRecord>> {
    const items = await this.readAgentsMock();
    const search = query.search?.trim().toLowerCase() ?? '';
    const accountStatus = query.accountStatus?.trim().toLowerCase() ?? '';
    const agentType = query.agentType?.trim().toLowerCase() ?? '';
    const agency = query.agency?.trim().toLowerCase() ?? '';
    const assignedAgencyId = query.assignedAgencyId?.trim().toLowerCase() ?? '';
    const page = Math.max(1, Number(query.page ?? 1));
    const limit = Math.max(1, Number(query.limit ?? 10));

    /**
     * BUSINESS LOGIC:
     * In server-search mode the shared table skips local filtering entirely, so the mock repository
     * has to honor the same query contract as the backend or search appears broken in local runs.
     */
    const filtered = items.filter((item) => {
      if (accountStatus && item.status.toLowerCase() !== accountStatus) {
        return false;
      }

      if (agentType && item.agentType.toLowerCase() !== agentType) {
        return false;
      }

      const matchesAgencyId = String(item.agencyId ?? '').trim().toLowerCase() === agency;
      const matchesAgencyName = item.agencyName.toLowerCase() === agency;

      if (agency && !matchesAgencyId && !matchesAgencyName) {
        return false;
      }

      if (assignedAgencyId && String(item.agencyId ?? '').trim().toLowerCase() !== assignedAgencyId) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        item.name,
        item.email,
        item.phoneDisplay,
        item.agencyName,
        item.city
      ].some((value) => String(value ?? '').toLowerCase().includes(search));
    });

    const start = (page - 1) * limit;
    return {
      items: filtered.slice(start, start + limit),
      total: filtered.length,
      page,
      limit
    };
  }


  override async getAgentById(id: string): Promise<AgentRecord | null> {
  const result = await this.getAgents();
  return result.items.find((item) => item.id === id) ?? null;
}


  override async getPendingAgentRequests(): Promise<AgentApprovalRequestRecord[]> {
    return this.readPendingAgentRequestsMock();
  }

  override async approveAgentRequest(id: string): Promise<AgentRecord | null> {
    const requests = await this.readPendingAgentRequestsMock();
    const request = requests.find((item) => item.id === id);

    if (!request) {
      return null;
    }

    const approved = toAgentRecord(`agt-approved-${id.replace('agt-req-', '')}`, {
      name: request.name,
      status: 'active',
      dealerId: request.dealerId,
      joinedAt: request.requestDate.split('T')[0] ?? formatIsoReviewDate(1),
      contact: request.contact,
      address: request.address,
      assignedAgencies: [request.requestedAgency],
      assignedPos: request.requestedPos,
      profile: {
        agent: request.agentProfile
      }
    });

    const agents = await this.readAgentsMock();
    this.agentsCache = [approved, ...agents];
    this.pendingAgentRequestsCache = requests.filter((item) => item.id !== id);
    return approved;
  }

  override async getPos(_query: PosListQuery = {}): Promise<PagedResult<PosRecord>> {
    const items = await this.readPosMock();
    return { items, total: items.length, page: 1, limit: items.length };
  }

  override async getPosById(id: string): Promise<PosRecord | null> {
    const records = await this.readPosMock();
    return records.find((item) => item.id === id) ?? null;
  }

  override async createPos(payload: PosUpsertPayload): Promise<PosRecord> {
    const records = await this.readPosMock();
    const created = toPosRecord(`pos-${Math.random().toString(16).slice(2, 8)}`, payload);
    this.posCache = [created, ...records];
    return created;
  }

  override async updatePos(id: string, payload: PosUpsertPayload): Promise<PosRecord | null> {
    const current = await this.getPosById(id);

    if (!current) {
      return null;
    }

    const updated = toPosRecord(id, payload, current);
    const records = await this.readPosMock();
    this.posCache = records.map((item) => (item.id === id ? updated : item));
    return updated;
  }

  override async deletePos(id: string): Promise<boolean> {
    const records = await this.readPosMock();
    const next = records.filter((item) => item.id !== id);
    const changed = next.length !== records.length;
    this.posCache = next;
    return changed;
  }

  override async getPosStockDetails(id: string): Promise<PosStockDetails> {
    const pos = await this.getPosById(id);
    return pos?.stockDetails ?? { stockOnHand: 0, reorderLevel: 0, stockStatus: 'ok', lastStockUpdate: '', notes: '', entries: [] };
  }

  override async getPosStockTransactions(id: string): Promise<PosStockEntry[]> {
    const pos = await this.getPosById(id);
    return pos?.stockDetails.entries ?? [];
  }

  override async getProspects(_query: ProspectListQuery = {}): Promise<PagedResult<ProspectRecord>> {
    const items = await this.readProspectsMock();
    return { items, total: items.length, page: 1, limit: items.length };
  }

  override async getProspectById(id: string): Promise<ProspectRecord | null> {
    const records = await this.readProspectsMock();
    return records.find((item) => item.id === id) ?? null;
  }

  override async createProspect(payload: ProspectUpsertPayload): Promise<ProspectRecord> {
    const records = await this.readProspectsMock();
    const created = toProspectRecord(`prospect-${Math.random().toString(16).slice(2, 8)}`, payload);
    this.prospectCache = [created, ...records];
    return created;
  }

  override async updateProspect(id: string, payload: ProspectUpsertPayload): Promise<ProspectRecord | null> {
    const current = await this.getProspectById(id);

    if (!current) {
      return null;
    }

    const updated = toProspectRecord(id, payload, current);
    const records = await this.readProspectsMock();
    this.prospectCache = records.map((item) => (item.id === id ? updated : item));
    return updated;
  }

  override async approveProspect(id: string, _payload: ProspectApprovalPayload): Promise<ProspectRecord | null> {
    const current = await this.getProspectById(id);

    if (!current) {
      return null;
    }

    const approved: ProspectRecord = {
      ...current,
      status: 'active',
      creationStatus: 'approved'
    };
    const records = await this.readProspectsMock();
    this.prospectCache = records.map((item) => (item.id === id ? approved : item));
    return approved;
  }

  override async getProspectStockDetails(id: string): Promise<PosStockDetails> {
    const prospect = await this.getProspectById(id);
    return prospect?.stockDetails ?? { stockOnHand: 0, reorderLevel: 0, stockStatus: 'ok', lastStockUpdate: '', notes: '', entries: [] };
  }

  override async getProspectStockTransactions(id: string): Promise<PosStockEntry[]> {
    const prospect = await this.getProspectById(id);
    return prospect?.stockDetails.entries ?? [];
  }

  override async getSupervisors(): Promise<PagedResult<SupervisorRecord>> {
    const supervisors = await this.readSupervisorsMock();
    const items = [...supervisors.approved];
    return { items, page: 1, limit: items.length, total: items.length };
  }

  override async getPendingSupervisorRequests(query: { page?: number; limit?: number; search?: string } = {}): Promise<PagedResult<SupervisorApprovalRequestRecord>> {
    const supervisors = await this.readSupervisorsMock();
    const search = query.search?.trim().toLowerCase() ?? '';
    const filtered = supervisors.pending.filter((item) => {
      if (!search) return true;
      return [item.name, item.email, item.approvalStatus].some((value) => value.toLowerCase().includes(search));
    });
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    const start = (page - 1) * limit;
    return {
      items: filtered.slice(start, start + limit),
      page,
      limit,
      total: filtered.length
    };
  }

  override async createSupervisor(payload: SupervisorUpsertPayload): Promise<SupervisorRecord> {
    const supervisors = await this.readSupervisorsMock();
    const record = toSupervisorRecord(`sup-${Math.random().toString(16).slice(2, 6)}`, payload);
    this.supervisorsCache = {
      approved: [record, ...supervisors.approved],
      pending: supervisors.pending
    };
    return record;
  }

  override async updateSupervisor(id: string, payload: SupervisorUpsertPayload): Promise<SupervisorRecord | null> {
    const supervisors = await this.readSupervisorsMock();
    const current = supervisors.approved.find((item) => item.id === id);

    if (!current) {
      return null;
    }

    const updated = toSupervisorRecord(id, payload, current);
    this.supervisorsCache = {
      approved: supervisors.approved.map((item) => (item.id === id ? updated : item)),
      pending: supervisors.pending
    };
    return updated;
  }

  override async approveSupervisorRequest(id: string, assignedAgencyId: string): Promise<SupervisorRecord | null> {
    const supervisors = await this.readSupervisorsMock();
    const request = supervisors.pending.find((item) => item.id === id);

    if (!request) {
      return null;
    }

    const agencies = await this.readAgenciesMock();
    const assignedAgency = agencies.find((item) => item.id === assignedAgencyId);

    if (!assignedAgency) {
      return null;
    }

    const approved = toSupervisorRecord(`sup-${id.replace('sup-req-', '')}`, {
      fullName: request.name,
      email: request.contact.email,
      contact: request.contact,
      address: {
        formattedAddress: assignedAgency.addressDetails?.formattedAddress ?? assignedAgency.address,
        streetAddress: assignedAgency.addressDetails?.streetAddress ?? '',
        locality: assignedAgency.addressDetails?.locality ?? assignedAgency.city,
        subLocality: assignedAgency.addressDetails?.subLocality ?? assignedAgency.area,
        region: assignedAgency.addressDetails?.region ?? '',
        country: assignedAgency.addressDetails?.country ?? '',
        postalCode: '',
        area: assignedAgency.addressDetails?.area ?? assignedAgency.area,
        geo: assignedAgency.addressDetails?.geo ?? [0, 0]
      },
      assignedAgencyId: assignedAgency.id,
      profile: {
        supervisor: {
          profile: 'Field Supervisor',
          code: `SUP-${request.id.slice(-3).toUpperCase()}`,
          assignedAgency: {
            agencyId: assignedAgency.id,
            agencyName: assignedAgency.name,
            city: assignedAgency.city,
            area: assignedAgency.area
          },
          guarantorContact1: { name: '', phoneNumber: '' },
          guarantorContact2: { name: '', phoneNumber: '' }
        }
      }
    });

    this.supervisorsCache = {
      approved: [approved, ...supervisors.approved],
      pending: supervisors.pending.filter((item) => item.id !== id)
    };

    const auth = await this.readAuthMock();
    this.authCache = {
      ...auth,
      users: auth.users.map((item) =>
        item.email.toLowerCase() === request.email.toLowerCase()
          ? {
              ...item,
              status: 'active',
              agency: assignedAgency.name,
              address: assignedAgency.address,
              phone: request.contact.phone.number,
              role: 'supervisor',
              userType: 'Field Supervisor'
            }
          : item
      )
    };

    return approved;
  }

  override async rejectSupervisorRequest(id: string): Promise<void> {
    const supervisors = await this.readSupervisorsMock();
    this.supervisorsCache = {
      approved: supervisors.approved,
      pending: supervisors.pending.filter((item) => item.id !== id)
    };
  }

  override async deleteSupervisor(ids: string[]): Promise<boolean> {
    const supervisors = await this.readSupervisorsMock();
    const next = supervisors.approved.filter((item) => !ids.includes(item.id));

    if (next.length === supervisors.approved.length) {
      return false;
    }

    this.supervisorsCache = {
      approved: next,
      pending: supervisors.pending
    };

    return true;
  }

  
  override async getTasks(_query: TaskListQuery = {}): Promise<PagedResult<TaskRecord>> {
    const items = await this.readTasksMock();
    return { items, total: items.length, page: 1, limit: items.length };
  }

  override async getTaskById(id: string): Promise<TaskRecord | null> {
    const items = await this.readTasksMock();
    return items.find((item) => item.id === id) ?? null;
  }

  override async getTargets(query: TargetListQuery = {}): Promise<PagedResult<AgentTargetRecord>> {
    const rows = await this.readTargetsMock();
    const search = String(query.search ?? '').trim().toLowerCase();
    const userType = query.userType;
    const filtered = rows.filter((row) => {
      if (userType && row.userBucket !== userType) return false;
      if (query.type && row.type !== query.type) return false;
      if (query.period && row.period !== query.period) return false;
      if (query.status && row.status !== query.status) return false;
      if (query.agencyId && row.agencyId !== query.agencyId) return false;
      if (query.targetUserId && row.targetUserId !== query.targetUserId) return false;
      if (!search) return true;
      return [
        row.targetUserName,
        row.agencyName,
        row.type,
        row.period,
        row.categoryName,
        row.productName
      ].some((value) => String(value ?? '').toLowerCase().includes(search));
    });
    const page = Number(query.page ?? 1);
    const limit = Number(query.limit ?? 10);
    const start = (page - 1) * limit;

    return {
      items: filtered.slice(start, start + limit),
      total: filtered.length,
      page,
      limit
    };
  }

  override async getTargetPerformanceAnalytics(query: TargetPerformanceAnalyticsQuery): Promise<AgentTargetAnalyticsResult> {
    const targets = await this.readTargetsMock();
    const page = Number(query.page ?? 1);
    const limit = Number(query.limit ?? 20);
    const start = (page - 1) * limit;
    const pagedTargets = targets.filter((target) => target.userBucket === query.userType).slice(start, start + limit);
    const targetTypes: AgentTargetRecord['type'][] = ['stock', 'float-sale', 'recharge-pos'];
    const titles: Record<string, string> = {
      stock: 'Stock sale',
      'float-sale': 'Injected float',
      'recharge-pos': 'Average for recharged POS'
    };

    return {
      sections: targetTypes.map((targetType) => {
        const rows = pagedTargets.flatMap((target) =>
          (target.targetSummary ?? [])
            .filter((summary) => summary.targetType === targetType)
            .map((summary) => {
              const mtdActual = target.progress.achieved;
              const mtdTarget = Number(summary.monthlyTarget ?? 0);
              return {
                id: target.id,
                userType: query.userType,
                userId: target.targetUserId,
                agencyId: target.agencyId,
                targetType,
                categoryId: summary.categoryId,
                productId: summary.productId,
                sourceStatus: target.status,
                dailyTarget: summary.dailyTarget,
                weeklyTarget: summary.weeklyTarget,
                monthlyTarget: summary.monthlyTarget,
                notes: target.notes,
                createdAt: target.createdAt,
                updatedAt: target.updatedAt,
                todayAchievement: target.progress.achieved,
                previousDay: 0,
                dodProgress: null,
                actualWeek: target.progress.achieved,
                previousWeek: 0,
                wowProgress: null,
                mtdActual,
                mtdPrevious: 0,
                momProgress: null,
                mtdTarget,
                actualVsMtdTarget: mtdTarget > 0 ? (mtdActual - mtdTarget) / mtdTarget : null,
                gapMtdActualVsMtdTarget: mtdActual - mtdTarget,
                userName: target.targetUserName,
                agencyName: target.agencyName,
                categoryName: summary.categoryName,
                productName: summary.productName
              };
            })
        );
        const total = rows.reduce((acc, row) => ({
          todayAchievement: acc.todayAchievement + row.todayAchievement,
          previousDay: acc.previousDay + row.previousDay,
          dodProgress: null,
          actualWeek: acc.actualWeek + row.actualWeek,
          previousWeek: acc.previousWeek + row.previousWeek,
          wowProgress: null,
          mtdActual: acc.mtdActual + row.mtdActual,
          mtdPrevious: acc.mtdPrevious + row.mtdPrevious,
          momProgress: null,
          mtdTarget: acc.mtdTarget + row.mtdTarget,
          actualVsMtdTarget: null,
          gapMtdActualVsMtdTarget: acc.gapMtdActualVsMtdTarget + row.gapMtdActualVsMtdTarget
        }), {
          todayAchievement: 0,
          previousDay: 0,
          dodProgress: null,
          actualWeek: 0,
          previousWeek: 0,
          wowProgress: null,
          mtdActual: 0,
          mtdPrevious: 0,
          momProgress: null,
          mtdTarget: 0,
          actualVsMtdTarget: null,
          gapMtdActualVsMtdTarget: 0
        });

        return {
          targetType,
          title: titles[targetType],
          sourceStatus: rows[0]?.sourceStatus ?? 'not-started',
          rows,
          total: {
            ...total,
            actualVsMtdTarget: total.mtdTarget > 0 ? (total.mtdActual - total.mtdTarget) / total.mtdTarget : null
          }
        };
      }),
      page,
      limit,
      total: targets.filter((target) => target.userBucket === query.userType).length
    };
  }

  override async exportTargetPerformanceAnalyticsDocument(query: TargetPerformanceAnalyticsDocumentQuery): Promise<TargetPerformanceAnalyticsDocumentExport> {
    const isPdf = query.documentType === 'pdf';
    return {
      fileName: `target-performance-analytics-${query.date}.${isPdf ? 'pdf' : 'xlsx'}`,
      mimeType: isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      base64: 'TW9jayB0YXJnZXQgcGVyZm9ybWFuY2UgYW5hbHl0aWNz'
    };
  }

  override async createTarget(payload: AgentTargetPayload): Promise<AgentTargetRecord> {
    const rows = await this.readTargetsMock();
    const agents = await this.readAgentsMock();
    const agent = agents.find((item) => item.authUserId === payload.targetUserId || item.id === payload.targetUserId);
    const record = this.toMockTargetRecord(`target-${Date.now()}`, payload, agent);
    this.targetsCache = [record, ...rows];
    return record;
  }

  override async updateTarget(id: string, payload: AgentTargetPayload): Promise<AgentTargetRecord> {
    const rows = await this.readTargetsMock();
    const agents = await this.readAgentsMock();
    const agent = agents.find((item) => item.authUserId === payload.targetUserId || item.id === payload.targetUserId);
    const current = rows.find((row) => row.id === id);
    const record = {
      ...this.toMockTargetRecord(id, payload, agent),
      createdAt: current?.createdAt ?? new Date().toISOString(),
      progress: current?.progress ?? this.toMockTargetRecord(id, payload, agent).progress,
      status: current?.status ?? 'not-started'
    };
    this.targetsCache = rows.map((row) => (row.id === id ? record : row));
    return record;
  }

  override async deleteTarget(id: string): Promise<void> {
    const rows = await this.readTargetsMock();
    this.targetsCache = rows.filter((row) => row.id !== id);
  }

  /**
   * BUSINESS LOGIC: readTargetsMock
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private async readTargetsMock(): Promise<AgentTargetRecord[]> {
    if (this.targetsCache) {
      return this.targetsCache;
    }

    const agents = await this.readAgentsMock();
    const rows = agents.slice(0, 8).map((agent, index) => {
      const type = cycleValue(['stock', 'recharge-pos', 'float-sale'] as const, index);
      const targetValue = cycleValue([40, 25, 60, 15] as const, index);
      const achieved = Math.min(targetValue, cycleValue([0, 12, 24, 40, 55] as const, index));
      const percent = targetValue > 0 ? Math.round((achieved / targetValue) * 100) : 0;
      const status = percent >= 100 ? 'met' : percent > 0 ? 'in-progress' : type === 'float-sale' ? 'pending-source' : 'not-started';

      return {
        id: `target-${index + 1}`,
        targetUserId: agent.authUserId || agent.id,
        targetUserName: agent.name,
        userBucket: 'AGENT' as const,
        agencyId: agent.agencyId,
        agencyName: agent.agencyName,
        type,
        period: cycleValue(['daily', 'weekly', 'monthly'] as const, index),
        targetValue,
        dailyTarget: Math.max(1, Math.round(targetValue / 20)),
        weeklyTarget: Math.max(1, Math.round(targetValue / 4)),
        monthlyTarget: targetValue,
        categoryId: type === 'stock' ? 'cat-airtime' : undefined,
        categoryName: type === 'stock' ? 'Airtime' : undefined,
        productId: type === 'stock' ? 'prod-sim-pack' : undefined,
        productName: type === 'stock' ? 'Starter SIM Pack' : undefined,
        notes: '',
        startDate: formatIsoReviewDate(index),
        endDate: formatIsoReviewDate(index + 7),
        progress: { achieved, target: targetValue, percent, status },
        status,
        createdAt: formatIsoReviewDate(index),
        updatedAt: formatIsoReviewDate(index)
      } satisfies AgentTargetRecord;
    });

    this.targetsCache = rows;
    return rows;
  }

  /**
   * BUSINESS LOGIC: toMockTargetRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toMockTargetRecord(id: string, payload: AgentTargetPayload, agent?: AgentRecord): AgentTargetRecord {
    const payloadSummary = payload.targetSummary ?? (payload.type ? [{
      targetType: payload.type,
      categoryId: payload.categoryId ?? null,
      productId: payload.productId ?? null,
      dailyTarget: Number(payload.dailyTarget ?? 0),
      weeklyTarget: Number(payload.weeklyTarget ?? 0),
      monthlyTarget: Number(payload.monthlyTarget ?? payload.targetValue ?? 0)
    }] : []);
    const firstSummary = payloadSummary[0];
    const dailyTarget = Number(firstSummary?.dailyTarget ?? 0);
    const weeklyTarget = Number(firstSummary?.weeklyTarget ?? 0);
    const monthlyTarget = Number(firstSummary?.monthlyTarget ?? payload.targetValue ?? 0);
    const targetValue = monthlyTarget || weeklyTarget || dailyTarget;
    return {
      id,
      targetUserId: payload.targetUserId,
      targetUserName: agent?.name ?? payload.targetUserId,
      userBucket: 'AGENT',
      agencyId: payload.agencyId ?? agent?.agencyId ?? '',
      agencyName: agent?.agencyName ?? '',
      type: (firstSummary?.targetType ?? payload.type ?? 'stock') as AgentTargetRecord['type'],
      period: (payload.period ?? 'monthly') as AgentTargetRecord['period'],
      targetValue,
      dailyTarget,
      weeklyTarget,
      monthlyTarget,
      categoryId: firstSummary?.categoryId ?? undefined,
      productId: firstSummary?.productId ?? undefined,
      targetSummary: payloadSummary.map((row, index) => ({
        id: `${id}-summary-${index + 1}`,
        targetType: row.targetType as AgentTargetRecord['type'],
        categoryId: row.categoryId ?? null,
        productId: row.productId ?? null,
        dailyTarget: Number(row.dailyTarget ?? 0),
        weeklyTarget: Number(row.weeklyTarget ?? 0),
        monthlyTarget: Number(row.monthlyTarget ?? 0)
      })),
      notes: payload.notes,
      progress: { achieved: 0, target: targetValue, percent: 0, status: 'not-started' },
      status: 'not-started',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  override getTaskActivities(query: TaskActivityListQuery = {}): Promise<PagedResult<TaskActivity>> {
    return Promise.resolve({
      items: [],
      total: 0,
      page: query.page ?? 1,
      limit: query.limit ?? 10
    });
  }

  /**
   * API NORMALIZATION:
   * Auth mock data is sourced from a static JSON file, but once loaded it becomes the mutable
   * session source for login, OTP, and signup simulations during the current app runtime.
   */
  private async readAuthMock(): Promise<AuthMockFile> {
    if (this.authCache) {
      return this.authCache;
    }

    this.authCache = await firstValueFrom(this.http.get<AuthMockFile>('assets/mock-data/auth.mock.json'));
    return this.authCache;
  }

  /**
   * BUSINESS LOGIC:
   * Agency seed rows are intentionally varied across city, area, stock status, and staffing counts
   * so list filters, dashboard cards, and supervisor scope flows all have realistic combinations.
   */
  private async readAgenciesMock(): Promise<AgencyRecord[]> {
    if (this.agenciesCache) {
      return this.agenciesCache;
    }

    const rows = buildReviewRows<AgencyRecord>(100, (index) => {
      const city = cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index);
      const area = cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index);
      const addressDetails = {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: city,
        subLocality: area,
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area,
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01] as [number, number]
      };
      const phoneNumber = `+237 67${index}${index + 2} ${index + 3}${index + 4}${index + 5}`;

      return {
        id: `ag-${index + 1}`,
        name: `Palladium Agency ${formatSequence(index)}`,
        code: `PA-${100 + index}`,
        city,
        area,
        address: addressDetails.formattedAddress,
        contact: phoneNumber,
        status: cycleValue(['active', 'inactive'] as const, index),
        stockStatus: cycleValue(['ok', 'low', 'out-of-stock'] as const, index),
        supervisors: 1 + (index % 3),
        agents: 4 + (index % 7),
        posCount: 2 + (index % 8),
        primaryContactName: cycleValue(['Amina Diallo', 'Kofi Mensah', 'Nadine Tamo'] as const, index),
        contactDetails: {
          email: `agency${index + 1}@palladium.africa`,
          phone: {
            dialCode: 237,
            iso2: 'cm',
            country: 'Cameroon',
            number: phoneNumber
          }
        },
        addressDetails,
        assignedSupervisors: [],
        assignedAgents: [],
        assignedBtlUsers: [],
        assignedLogisticsUsers: [],
        assignedPos: []
      };
    });

    this.agenciesCache = rows;
    return this.agenciesCache;
  }

  /**
   * BUSINESS LOGIC:
   * Supervisor mock data is split between approved and pending buckets because multiple screens
   * depend on that lifecycle distinction for list rendering and approval actions.
   */
  private async readSupervisorsMock(): Promise<SupervisorsMockFile> {
    if (this.supervisorsCache) {
      return this.supervisorsCache;
    }

    const approved = buildReviewRows(100, (index) => ({
      id: `sup-${index + 1}`,
      name: cycleValue(['Kofi Mensah', 'Nadine Tamo', 'Yao Konate'] as const, index),
      status: cycleValue(['active', 'inactive'] as const, index),
      contact: {
        email: `supervisor${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `67${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      assignedAgencies: [],
      profile: {
        supervisor: {
          profile: cycleValue(['Regional Supervisor', 'Agency Supervisor', 'Field Supervisor'] as const, index),
          code: `SUP-${100 + index}`,
          guarantorContact1: {
            name: cycleValue(['Monique Njoya', 'Stephane Talla', 'Irine Ndzi'] as const, index),
            phoneNumber: `65000${String(index + 21).padStart(4, '0')}`
          },
          guarantorContact2: {
            name: cycleValue(['Denis Fotso', 'Leonie Meka', 'Chris Ndzi'] as const, index),
            phoneNumber: `66000${String(index + 21).padStart(4, '0')}`
          }
        }
      }
    })).map((row) => toSupervisorRecord(row.id, row as any));

    const pending = buildReviewRows(3, (index) => ({
      id: `sup-req-${index + 1}`,
      name: cycleValue(['Eric Mvoula', 'Clarisse Niba', 'Fabrice Simo'] as const, index),
      email: `candidate${index + 1}@palladium.africa`,
      requestDate: formatDisplayReviewDate(index + 1),
      contact: {
        email: `candidate${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `66${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      requestedAgency: {
        id: `ag-${index + 1}`,
        name: `Palladium Agency ${formatSequence(index + 1)}`,
        city: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)
      },
      approvalStatus: 'pending'
    }));

    this.supervisorsCache = {
      approved,
      pending: pending as any
    };

    return this.supervisorsCache as SupervisorsMockFile;
  }

  /**
   * BUSINESS LOGIC:
   * Agent seed rows mirror the live add/edit model closely enough to exercise assignment, profile,
   * and dashboard views without requiring a backend.
   */
  private async readAgentsMock(): Promise<AgentRecord[]> {
    if (this.agentsCache) {
      return this.agentsCache;
    }

    const rows = buildReviewRows(100, (index) => ({
      id: `agt-${index + 1}`,
      name: cycleValue(['Eli Tchoumi', 'Grace Nfor', 'Ismael Kone'] as const, index),
      status: cycleValue(['active', 'inactive'] as const, index),
      dealerId: `DLR-${600 + index}`,
      joinedAt: formatIsoReviewDate(index + 2),
      contact: {
        email: `agent${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `65${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      assignedAgencies: [],
      assignedPos: [],
      profile: {
        agent: {
          agentType: cycleValue(['Employee', 'Contract worker', 'Intern'] as const, index),
          guarantorContact1: {
            name: cycleValue(['Alice Mbanga', 'Roland Tamo', 'Ethel Ndzi'] as const, index),
            phoneNumber: `67000${String(index + 11).padStart(4, '0')}`
          },
          guarantorContact2: {
            name: cycleValue(['Brice Ewane', 'Lina Fokou', 'Serge Meka'] as const, index),
            phoneNumber: `68000${String(index + 11).padStart(4, '0')}`
          }
        }
      }
    }));

    this.agentsCache = rows.map((row) => toAgentRecord(row.id, row as any));
    return this.agentsCache;
  }

  /**
   * BUSINESS LOGIC:
   * Pending agent requests intentionally preserve requested-agency and profile fields so approval
   * screens can validate the same shape they expect from the live backend flow.
   */
  private async readPendingAgentRequestsMock(): Promise<AgentApprovalRequestRecord[]> {
    if (this.pendingAgentRequestsCache) {
      return this.pendingAgentRequestsCache;
    }

    this.pendingAgentRequestsCache = buildReviewRows(4, (index) => ({
      id: `agt-req-${index + 1}`,
      name: cycleValue(['Serge Ewane', 'Linda Fotso', 'Pauline Ebong'] as const, index),
      authUserId: `candidate-agent${index + 1}@palladium.africa`,
      dealerId: `DLR-${700 + index}`,
      requestDate: formatIsoReviewDate(index + 2),
      contact: {
        email: `candidate-agent${index + 1}@palladium.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `64${index}${index + 2}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}`
        }
      },
      address: {
        formattedAddress: `${cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)}, ${cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index)}`,
        streetAddress: 'Main Street',
        locality: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        subLocality: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        region: cycleValue(['Littoral', 'Centre', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index),
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      requestedAgency: {
        id: `ag-${index + 1}`,
        name: `Palladium Agency ${formatSequence(index + 1)}`,
        city: cycleValue(['Douala', 'Yaounde', 'Bafoussam'] as const, index),
        area: cycleValue(['Bonapriso', 'Akwa', 'Bastos'] as const, index)
      },
      requestedPos: [],
      agentType: cycleValue(['Employee', 'Contract worker', 'Intern'] as const, index),
      agentProfile: {
        ...DEFAULT_AGENT_PROFILE,
        agentType: cycleValue(['Employee', 'Contract worker', 'Intern'] as const, index)
      }
    })) as any;

    return this.pendingAgentRequestsCache as AgentApprovalRequestRecord[];
  }

  /**
   * API NORMALIZATION:
   * POS mock rows start from compact seed tuples and are then expanded into the richer PosRecord
   * shape consumed by list screens, detail views, and stock tabs.
   */
  private async readPosMock(): Promise<PosRecord[]> {
    if (this.posCache) {
      return this.posCache;
    }

    const seedRows: Array<[string, string, 'Cabine' | 'Level 2', 'High Value' | 'Regular', 'active' | 'inactive', 'active' | 'inactive', string, string, string, string, number, number, 'ok' | 'low' | 'out-of-stock', string]> = [
      ['pos-1', 'Akwa Express POS', 'Level 2', 'High Value', 'active', 'active', 'Douala', 'Akwa', 'Mireille Tamo', 'Agency Douala Central', 1240, 400, 'ok', '2026-04-30'],
      ['pos-2', 'Bastos Market POS', 'Cabine', 'Regular', 'active', 'active', 'Yaounde', 'Bastos', 'Eric Mvondo', 'Agency Yaounde East', 320, 250, 'ok', '2026-04-29'],
      ['pos-3', 'Bonaberi Dealer Point', 'Cabine', 'Regular', 'active', 'inactive', 'Douala', 'Bonaberi', 'Aline Ngono', 'Agency Bonaberi', 185, 220, 'low', '2026-04-28'],
      ['pos-4', 'Molyko Campus POS', 'Level 2', 'High Value', 'active', 'active', 'Buea', 'Molyko', 'Patrick Ewane', 'Agency Buea', 980, 350, 'ok', '2026-04-27'],
      ['pos-5', 'Limbe Mile Four POS', 'Cabine', 'Regular', 'inactive', 'inactive', 'Limbe', 'Mile Four', 'Grace Nsom', 'Agency Limbe', 0, 180, 'out-of-stock', '2026-04-20'],
      ['pos-6', 'Bafoussam Centre POS', 'Level 2', 'Regular', 'active', 'active', 'Bafoussam', 'Centre Ville', 'Jean Kuete', 'Agency Bafoussam', 410, 300, 'ok', '2026-04-30'],
      ['pos-7', 'Deido Junction POS', 'Cabine', 'Regular', 'active', 'inactive', 'Douala', 'Deido', 'Nadia Essomba', 'Agency Douala Central', 94, 200, 'low', '2026-04-26'],
      ['pos-8', 'Melen Float POS', 'Level 2', 'High Value', 'active', 'active', 'Yaounde', 'Melen', 'Samuel Owona', 'Agency Yaounde West', 1510, 500, 'ok', '2026-04-29'],
      ['pos-9', 'Nkongsamba Trade POS', 'Cabine', 'Regular', 'inactive', 'inactive', 'Nkongsamba', 'Commercial Avenue', 'Brenda Fokam', 'Agency Nkongsamba', 38, 150, 'low', '2026-04-18'],
      ['pos-10', 'Bonapriso Premium POS', 'Level 2', 'High Value', 'active', 'active', 'Douala', 'Bonapriso', 'Cedric Manga', 'Agency Douala Central', 1760, 650, 'ok', '2026-05-01'],
      ['pos-11', 'Etoudi Neighborhood POS', 'Cabine', 'Regular', 'active', 'active', 'Yaounde', 'Etoudi', 'Marthe Abena', 'Agency Yaounde North', 260, 240, 'ok', '2026-04-25'],
      ['pos-12', 'Kumba Main Road POS', 'Level 2', 'Regular', 'active', 'inactive', 'Kumba', 'Main Road', 'Frank Besong', 'Agency Kumba', 72, 180, 'low', '2026-04-24']
    ];
    const expandedSeedRows = [
      ...seedRows,
      ...buildReviewRows(88, (index) => {
        const sequence = index + seedRows.length + 1;
        const city = cycleValue(['Douala', 'Yaounde', 'Buea', 'Bafoussam', 'Limbe'] as const, index);
        const area = cycleValue(['Akwa', 'Bastos', 'Molyko', 'Centre Ville', 'Mile Four'] as const, index);
        const stockOnHand = 80 + ((index * 37) % 1700);
        return [
          `pos-${sequence}`,
          `${area} Retail POS ${formatSequence(sequence)}`,
          cycleValue(['Cabine', 'Level 2'] as const, index),
          cycleValue(['High Value', 'Regular'] as const, index),
          cycleValue(['active', 'inactive'] as const, index),
          cycleValue(['active', 'inactive'] as const, index + 1),
          city,
          area,
          cycleValue(['Mireille Tamo', 'Eric Mvondo', 'Aline Ngono', 'Patrick Ewane'] as const, index),
          `Agency ${city}`,
          stockOnHand,
          0,
          cycleValue(['ok', 'low', 'out-of-stock'] as const, index),
          formatIsoReviewDate(index + 1)
        ] as [string, string, 'Cabine' | 'Level 2', 'High Value' | 'Regular', 'active' | 'inactive', 'active' | 'inactive', string, string, string, string, number, number, 'ok' | 'low' | 'out-of-stock', string];
      })
    ];
    const rows = expandedSeedRows.map(([id, name, profile, posType, status, operationalStatus, city, area, agent, agency, stockOnHand, reorderLevel, stockStatus, lastStockUpdate], index) => ({
      id,
      name,
      profile,
      posType,
      status,
      operationalStatus,
      assignedAgentAuthUserId: `agent-${index + 1}@palladium.africa`,
      assignedAgentName: agent,
      contact: {
        email: `${String(name).toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/\.$/, '')}@external-pos.africa`,
        phone: {
          dialCode: 237,
          iso2: 'cm',
          country: 'Cameroon',
          number: `67${index + 1}${index + 3}${index + 4}${index + 5}${index + 6}${index + 7}${index + 8}`
        }
      },
      address: {
        formattedAddress: `${area}, ${city}`,
        streetAddress: `${area} Main Road`,
        locality: city,
        subLocality: area,
        region: cycleValue(['Littoral', 'Centre', 'Southwest', 'West'] as const, index),
        country: 'Cameroon',
        postalCode: '00000',
        area,
        geo: [4.05 + index * 0.01, 9.7 + index * 0.01]
      },
      stockDetails: {
        stockOnHand: Number(stockOnHand),
        reorderLevel: Number(reorderLevel),
        stockStatus,
        lastStockUpdate,
        notes: `External POS managed by ${agent} through ${agency}. POS does not use the app in phase 1.`,
        entries: [
          {
            id: `${id}-entry-1`,
            dateTime: `${lastStockUpdate} 09:30`,
            agent,
            productAsset: 'MTN 4G SIM',
            quantity: Math.max(10, Math.round(Number(stockOnHand) / 4)),
            entryStatus: 'Verified',
            remarks: 'Agent-entered stock count',
            proofRef: `${String(id).toUpperCase()}-PROOF`,
            balanceAfter: Number(stockOnHand)
          }
        ]
      }
    }));

    const posRows: PosRecord[] = rows.map((row, index) => ({
      ...toPosRecord(row.id, row as any),
      code: `POS-${String(index + 1).padStart(4, '0')}`,
      assignedAgentAuthUserId: row.assignedAgentAuthUserId,
      assignedAgentName: row.assignedAgentName,
      primaryAgent: row.assignedAgentName,
      primaryAgency: String(row.stockDetails.notes).match(/through (.*?)\./)?.[1] ?? 'Not assigned',
      lastVisitDate: row.stockDetails.lastStockUpdate,
      createdDate: row.stockDetails.lastStockUpdate
    }));
    this.posCache = posRows;
    return this.posCache;
  }

  /**
   * BUSINESS LOGIC:
   * The legacy task module only needs lightweight task rows for dashboard/list usage, so the mock
   * dataset stays intentionally small in shape even though the screen count is large.
   */
  private async readTasksMock(): Promise<TaskRecord[]> {
    if (this.tasksCache) {
      return this.tasksCache;
    }

    this.tasksCache = buildReviewRows(100, (index) => ({
      id: `tsk-${index + 1}`,
      title: `Task ${formatSequence(index + 1)}`,
      status: cycleValue(['open', 'in-progress', 'completed'] as const, index),
      dueDate: formatIsoReviewDate(index + 3)
    })) as any;

    return this.tasksCache as TaskRecord[];
  }

  /**
   * BUSINESS LOGIC:
   * Prospects default to an empty mock set so create flows and empty states can be exercised
   * cleanly until a richer seeded prospect dataset is needed.
   */
  private async readProspectsMock(): Promise<ProspectRecord[]> {
    if (this.prospectCache) {
      return this.prospectCache;
    }

    this.prospectCache = [];
    return this.prospectCache;
  }

  override getUsers(_params?: UsersListParams): Promise<UsersListResult> {
    return Promise.resolve({ items: [], total: 0, page: 1, limit: 100 });
  }

  override getUserById(_id: string): Promise<ApiUserSummary | null> {
    return Promise.resolve(null);
  }

  /**
   * API NORMALIZATION:
   * The mock dashboard summaries default to zeroed aggregates unless a feature explicitly seeds a
   * richer scenario. That keeps unsupported dashboard sections stable instead of inventing partial
   * numbers that conflict with seeded list data.
   */
  override getDashboardActiveSummary(_query: { assignedAgencyId?: string } = {}): Promise<DashboardActiveSummary> {
    return Promise.resolve({
      activeAgents: 0,
      btlUsers: 0,
      logisticsUsers: 0,
      supervisors: 0
    });
  }

  override getDashboardSummary(_query: { agencyId?: string } = {}): Promise<DashboardSummary> {
    return Promise.resolve({
      activePos: 0,
      activeProspects: 0,
      activeAgencies: 0,
      openAgentTasks: 0,
      openBtlCampaigns: 0,
      openLogisticsTasks: 0,
      totalStockCount: 0,
      centralInventory: []
    });
  }

  override exportDashboardPdf(_query: { agencyId?: string } = {}): Promise<DashboardPdfExport> {
    return Promise.resolve({
      fileName: 'mock-dashboard.pdf',
      mimeType: 'application/pdf',
      base64: 'TW9jayBkYXNoYm9hcmQgUERG'
    });
  }

  override exportSupervisorDashboardPdf(): Promise<DashboardPdfExport> {
    return Promise.resolve({
      fileName: 'mock-supervisor-dashboard.pdf',
      mimeType: 'application/pdf',
      base64: 'TW9jayBzdXBlcnZpc29yIGRhc2hib2FyZCBQREY='
    });
  }

  override getSupervisorDashboardSummary(query: { agentAuthUserId?: string; targetPeriod?: 'daily' | 'weekly' | 'monthly'; targetDate?: string } = {}): Promise<SupervisorDashboardSummary> {
    return Promise.resolve({
      activePos: 0,
      activeProspects: 0,
      openAgentTasks: 0,
      openBtlCampaigns: 0,
      openLogisticsTasks: 0,
      agencyInventory: [],
      supervisorWorkforce: {
        agency: null,
        activeAgents: 0,
        activeLogisticsUsers: 0,
        activeBtlUsers: 0
      },
      supervisorPendingWork: {
        agentTasks: 0,
        logisticsTasks: 0,
        btlActivities: 0
      },
      supervisorPendingApprovals: {
        total: 0,
        agentTaskApprovals: 0,
        logisticsTaskApprovals: 0,
        btlSubmissionApprovals: 0,
        fuelRequestApprovals: 0,
        maintenanceRequestApprovals: 0,
        routeChangeRequestApprovals: 0,
        prospectApprovals: 0
      },
      ...(query.agentAuthUserId
        ? {
            agentPerformance: {
              agent: { id: query.agentAuthUserId, name: 'Mock Agent' },
              tasks: {
                total: { value: 0 },
                open: { value: 0 },
                inProgress: { value: 0 },
                completed: { value: 0 },
                recent: []
              },
              stockItems: [],
              targets: [],
              achievements: {
                assignedTargetValue: { value: 0 },
                achievedValue: { value: 0 },
                remainingValue: { value: 0 },
                averageAchievementPercent: { value: 0 }
              }
            }
          }
        : {})
    });
  }
    override getAdminById(_id: string): Promise<ApiUserSummary | null> {
    return Promise.resolve(null);
  }


  override updateUserProfile(_id: string, _payload: UpdateUserProfilePayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateAdminUser(_id: string, _payload: UpdateUserProfilePayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateAgent(_id: string, _payload: UpdateUserProfilePayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteAgent(_ids: string[]): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
   override deleteAgency(_id: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
  override changeAgentPassword(_id: string, _newPassword: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
  override changeLogisticsPassword(_id: string, _newPassword: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

   override changeBtlPassword(_id: string, _newPassword: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }


  override changeAdminPassword(_id: string, _newPassword: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override changeSupervisorPassword(_id: string, _newPassword: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createUser(_payload: CreateUserPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createAdminUser(_payload: CreateUserPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createAgent(_payload: CreateUserPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  
  override createBtlUser(_payload: CreateUserPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createLogisticsUser(_payload: CreateUserPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateLogisticsUser(_id: string, _payload: UpdateUserProfilePayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateUserStatus(_id: string, _payload: UpdateUserStatusPayload): Promise<ApiUserSummary> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createTask(_payload: CreateTaskPayload): Promise<TaskRecord> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateTask(_id: string, _payload: UpdateTaskPayload): Promise<TaskRecord> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteTask(_id: string): Promise<void> {
    return Promise.resolve();
  }

   override deleteCategory(_id: string): Promise<void> {
    return Promise.resolve();
  }
  override getInventoryCategories(_search?: string): Promise<InventoryCategory[]> {
    return Promise.resolve([]);
  }

  override getInventoryCategoriesPaged(query: { search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<InventoryCategory>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override getInventoryProducts(_query?: { categoryId?: string; search?: string }): Promise<InventoryProduct[]> {
    return Promise.resolve([]);
  }

  override getInventoryProductsPaged(query: { categoryId?: string; search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<InventoryProduct>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override getInventoryVariants(_query?: { productId?: string; categoryId?: string; search?: string }): Promise<InventoryVariant[]> {
    return Promise.resolve([]);
  }

  override getInventoryVariantsPaged(query: { productId?: string; categoryId?: string; search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<InventoryVariant>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override exportInventoryCatalogExcel(): Promise<InventoryCatalogExcelExport> {
    const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    return Promise.resolve({
      fileName: 'mock-inventory-catalog.xlsx',
      mimeType,
      base64: 'TW9jayBpbnZlbnRvcnkgY2F0YWxvZw=='
    });
  }

  override getDefaultCentralReference(): Promise<CentralReference> {
    return Promise.resolve({ id: 'CENTRAL-001', name: 'Central Warehouse' });
  }

  override getCashBalances(query: CashBalanceListQuery = {}): Promise<PagedResult<CashBalance>> {
    return Promise.resolve({
      items: [],
      page: query.page ?? 1,
      limit: query.limit ?? 10,
      total: 0
    });
  }

  override getCashBalance(ownerType: CashOwnerType, ownerId: string): Promise<CashBalance | null> {
    return Promise.resolve({
      ownerType,
      ownerId,
      balance: 0,
      updatedAt: new Date().toISOString()
    });
  }

  override getCashMovements(query: CashMovementListQuery = {}): Promise<PagedResult<CashMovement>> {
    return Promise.resolve({
      items: [],
      page: query.page ?? 1,
      limit: query.limit ?? 10,
      total: 0
    });
  }

  override createCashMovement(payload: CashMovementPayload): Promise<CashMovement> {
    return Promise.resolve({
      id: `mock-cash-${Date.now()}`,
      movementType: payload.movementType,
      amount: payload.amount,
      direction: payload.direction ?? 'neutral',
      source: payload.sourceOwnerType && payload.sourceOwnerId
        ? { ownerType: payload.sourceOwnerType, ownerId: payload.sourceOwnerId }
        : payload.ownerType && payload.ownerId
          ? { ownerType: payload.ownerType, ownerId: payload.ownerId }
        : undefined,
      destination: payload.destinationOwnerType && payload.destinationOwnerId
        ? { ownerType: payload.destinationOwnerType, ownerId: payload.destinationOwnerId }
        : undefined,
      note: payload.note ?? undefined,
      referenceId: payload.referenceId,
      createdAt: payload.date ?? new Date().toISOString(),
      performedByName: 'System Administrator'
    });
  }

  override getAgentAssignedStock(_agentId: string): Promise<AgentInventoryStockCategoryGroup[]> {
    return Promise.resolve([]);
  }

  override getAgentStockTransactions(_agentId: string): Promise<AgentInventoryTransactionRecord[]> {
    return Promise.resolve([]);
  }

  override updateAgentStockVariant(_agencyId: string, _agentAuthUserId: string, _variantId: string, _payload: UpdateAgentStockVariantPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override deleteAgentStockCategory(_agentId: string, _categoryId: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override getAgencyAssignedStock(_agencyId: string): Promise<AgencyInventoryStockCategoryGroup[]> {
    return Promise.resolve([]);
  }

  override getCentralStockOverview(_filters?: { categoryId?: string; productId?: string; search?: string; page?: number; limit?: number }): Promise<CentralStockOverviewData | null> {
    return Promise.resolve(null);
  }

  override getCentralStockList(_centralId?: string, query: { search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<CentralStockListRow>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override getCentralTransfers(query: { status?: 'pending' | 'in-transit' | 'delivered'; page?: number; limit?: number; search?: string } = {}): Promise<PagedResult<CentralTransferListRow>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override getCentralBankingNotes(query: { search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<CentralBankingNoteRow>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 20, total: 0 });
  }

  override createCentralBankingNote(_payload: CentralBankingNotePayload): Promise<CentralBankingNoteRow | null> {
    return Promise.resolve(null);
  }

  override updateCentralBankingNote(_id: string, _payload: CentralBankingNotePayload): Promise<CentralBankingNoteRow | null> {
    return Promise.resolve(null);
  }

  override deleteCentralBankingNote(_id: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override async getAgencyReferences(search = ''): Promise<AgencyReferenceOption[]> {
    const agencies = await this.readAgenciesMock();
    const query = search.trim().toLowerCase();

    return agencies
      .filter((agency) => !query || agency.name.toLowerCase().includes(query))
      .map((agency) => ({ id: agency.id, name: agency.name }));
  }

  override getStockThresholds(query: StockThresholdQuery = {}): Promise<PagedResult<StockThresholdRow>> {
    const institutionType = query?.institutionType?.trim();
    const referenceId = query?.referenceId?.trim();
    const categoryId = query?.categoryId?.trim();
    const productId = query?.productId?.trim();
    const search = query?.search?.trim().toLowerCase();

    const filtered = this.stockThresholdsCache.filter((row) => {
        if (institutionType && row.institutionType !== institutionType) {
          return false;
        }

        if (referenceId && row.referenceId !== referenceId) {
          return false;
        }

        if (categoryId && row.categoryId !== categoryId) {
          return false;
        }

        if (productId && row.productId !== productId) {
          return false;
        }

        if (search && !`${row.referenceName} ${row.category} ${row.product}`.toLowerCase().includes(search)) {
          return false;
        }

        return true;
      });
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    const start = (page - 1) * limit;

    return Promise.resolve({
      items: filtered.slice(start, start + limit),
      page,
      limit,
      total: filtered.length
    });
  }

  override createStockThreshold(payload: CreateStockThresholdPayload): Promise<StockThresholdRow | null> {
    const nextRow: StockThresholdRow = {
      id: `threshold-${Date.now()}`,
      institutionType: payload.institutionType,
      referenceId: payload.referenceId,
      referenceName: 'Agency',
      categoryId: payload.categoryId,
      category: 'Category',
      productId: payload.productId,
      product: 'Product',
      quantity: payload.quantity,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    this.stockThresholdsCache = [nextRow, ...this.stockThresholdsCache];
    return Promise.resolve(nextRow);
  }

  override updateStockThreshold(id: string, payload: CreateStockThresholdPayload): Promise<StockThresholdRow | null> {
    const existing = this.stockThresholdsCache.find((row) => row.id === id);
    if (!existing) {
      return Promise.resolve(null);
    }

    const updated: StockThresholdRow = {
      ...existing,
      institutionType: payload.institutionType,
      referenceId: payload.referenceId,
      categoryId: payload.categoryId,
      productId: payload.productId,
      quantity: payload.quantity,
      updatedAt: new Date().toISOString()
    };

    this.stockThresholdsCache = this.stockThresholdsCache.map((row) => (row.id === id ? updated : row));
    return Promise.resolve(updated);
  }

  override deleteStockThreshold(id: string): Promise<boolean> {
    this.stockThresholdsCache = this.stockThresholdsCache.filter((row) => row.id !== id);
    return Promise.resolve(true);
  }

  override uploadFile(file: File, _context = 'common'): Promise<UploadedFileResult> {
    return Promise.resolve({
      url: `https://s3.example.com/uploads/${encodeURIComponent(file.name)}`,
      fileName: file.name
    });
  }

  override addCentralStock(_payload: CentralAddStockPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override getAgencyStockTransactions(_agencyId: string): Promise<AgencyInventoryTransactionRecord[]> {
    return Promise.resolve([]);
  }

  override getAgencyDirectSalesLogs(query: AgencyDirectSalesLogQuery): Promise<PagedResult<AgencyDirectSalesLogRecord>> {
    const page = Math.max(1, Number(query.page ?? 1));
    const limit = Math.max(1, Number(query.limit ?? 10));

    return Promise.resolve({
      items: [],
      total: 0,
      page,
      limit
    });
  }

  override addAgencyStockTransaction(_payload: AgencyInventoryTransactionPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override updateAgencyStockVariant(_agencyId: string, _variantId: string, _payload: UpdateAgencyStockVariantPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override deleteAgencyStockCategory(_agencyId: string, _categoryId: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override createCategory(_payload: CreateCategoryPayload): Promise<InventoryCategory> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateCategory(_id: string, _payload: CreateCategoryPayload): Promise<InventoryCategory> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createProduct(_payload: CreateProductPayload): Promise<InventoryProduct> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateProduct(_id: string, _payload: CreateProductPayload): Promise<InventoryProduct> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteProduct(_id: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createVariant(_payload: CreateVariantPayload): Promise<InventoryVariant> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateVariant(_id: string, _payload: CreateVariantPayload): Promise<InventoryVariant> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteVariant(_id: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override getBtlUsers(_query: { page?: number; limit?: number; search?: string; accountStatus?: string; approvalStatus?: string; agency?: string; agencyId?: string } = {}): Promise<PagedResult<BtlUserApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override getBtlUserById(_id: string): Promise<BtlUserApiRecord | null> {
    return Promise.resolve(null);
  }

  override getBtlCampaignById(_id: string): Promise<BtlCampaignApiRecord | null> {
    return Promise.resolve(null);
  }

  override getBtlCampaigns(_query: BtlCampaignListQuery = {}): Promise<PagedResult<BtlCampaignListItem>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override getBtlDashboardSummary(): Promise<BtlDashboardSummary> {
    return Promise.resolve({
      totalBtlUsers: { value: 0, secondaryValue: 0 },
      activeCampaigns: { value: 0, secondaryValue: 0 },
      pendingActivityReviews: { value: 0, secondaryValue: 0 },
      targetAchievement: { achieved: 0, target: 0, percent: 0 }
    });
  }

  override createBtlCampaign(_payload: BtlCampaignUpsertPayload): Promise<BtlCampaignApiRecord | null> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateBtlCampaign(_id: string, _payload: BtlCampaignUpsertPayload): Promise<BtlCampaignApiRecord | null> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteBtlCampaign(_id: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override getLogisticsUsers(_query: { page?: number; limit?: number; search?: string; accountStatus?: string; approvalStatus?: string; agency?: string; agencyId?: string } = {}): Promise<PagedResult<LogisticsUserApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override getLogisticsUserById(_id: string): Promise<LogisticsUserApiRecord | null> {
    return Promise.resolve(null);
  }

  override getLogisticsDashboardSummary(): Promise<LogisticsDashboardSummary> {
    return Promise.resolve({
      totalLogisticsUsers: { value: 0, secondaryValue: 0 },
      activeUsers: { value: 0, secondaryValue: 0 },
      usersOnRoute: { value: 0, secondaryValue: 0 },
      pendingApprovals: { value: 0, secondaryValue: 0 },
      assignedVehicles: { value: 0, secondaryValue: 0 },
      logisticsTasks: { totalTasks: 0, openTasks: 0, pendingApproval: 0, closedApprovalPending: 0 },
      fuelManagement: { fuelEntries: 0, pendingApproval: 0, approvedEntries: 0, fuelCardUsage: 0, linkedTaskEntries: 0 },
      vehicleManagement: { allVehicles: 0, activeVehicles: 0, assignedVehicles: 0, vehiclesWithDocuments: 0, vehiclesWithoutDocuments: 0 },
      vehicleMaintenance: { maintenanceRequests: 0, pendingReview: 0, highPriority: 0, completedRepairs: 0, vehicleLinkedRequests: 0, distinctVehicles: 0 }
    });
  }

  override getLogisticsTasks(query: LogisticsTaskListQuery = {}): Promise<PagedResult<LogisticsTaskListItem>> {
    const items: LogisticsTaskListItem[] = [
      {
        id: 'LT-8001',
        title: 'Douala Stock Movement To Agency Akwa',
        assignedToAuthUserId: 'LU-001',
        assignedToName: 'Emile Nchouta',
        vehicleId: 'V-1001',
        vehicleName: 'Hilux Fleet A (LT-2201)',
        createdByName: 'Admin Operations',
        status: 'IN_PROGRESS',
        parallelLegs: false,
        scheduledDate: '2026-05-18T08:15:00.000Z',
        note: 'Task created from admin panel so the logistics user can load, move, prove pickup, and prove delivery per route.',
        legCount: 2,
        completedLegCount: 1,
        lastActivityAt: '2026-05-18T16:20:00.000Z',
        createdAt: '2026-05-18T08:00:00.000Z',
        updatedAt: '2026-05-18T16:20:00.000Z'
      },
      {
        id: 'LT-8002',
        title: 'Yaounde Field Crew Movement',
        assignedToAuthUserId: 'LU-002',
        assignedToName: 'Lydie Mbianda',
        vehicleId: 'V-1002',
        vehicleName: 'Nissan Fleet B (LT-2207)',
        createdByName: 'Supervisor Ibrahim T.',
        status: 'PENDING',
        parallelLegs: false,
        scheduledDate: '2026-05-18T09:00:00.000Z',
        note: 'Transport technicians to campaign support stops and capture route arrival actions.',
        legCount: 1,
        completedLegCount: 0,
        createdAt: '2026-05-18T09:00:00.000Z',
        updatedAt: '2026-05-18T09:05:00.000Z'
      },
      {
        id: 'LT-8003',
        title: 'Bamenda Cash Escort And Restock',
        assignedToAuthUserId: 'LU-003',
        assignedToName: 'Joel Mbarga',
        vehicleId: 'V-1003',
        vehicleName: 'Isuzu D-Max C (LT-2288)',
        createdByName: 'Admin Operations',
        status: 'PENDING',
        parallelLegs: false,
        scheduledDate: '2026-05-17T08:30:00.000Z',
        note: 'Mixed movement with cash escort and product restock. Route not started because vehicle repair is pending.',
        legCount: 1,
        completedLegCount: 0,
        createdAt: '2026-05-17T08:30:00.000Z',
        updatedAt: '2026-05-17T11:15:00.000Z'
      }
    ];
    const search = (query.search ?? '').trim().toLowerCase();
    const filtered = search
      ? items.filter((item) =>
          [item.title, item.assignedToName, item.vehicleName, item.createdByName, item.note]
            .filter((value): value is string => Boolean(value))
            .some((value) => value.toLowerCase().includes(search))
        )
      : items;
    const page = query.page ?? 1;
    const limit = query.limit ?? 20;
    const start = (page - 1) * limit;
    return Promise.resolve({
      items: filtered.slice(start, start + limit),
      total: filtered.length,
      page,
      limit
    });
  }

  override getLogisticsTaskById(id: string): Promise<LogisticsTaskDetailApiRecord | null> {
    const list = this.getLogisticsTasks({ page: 1, limit: 1000 });
    return list.then((result) => {
      const item = result.items.find((entry) => entry.id === id);
      if (!item) {
        return null;
      }

      return {
        ...item,
        legs: []
      };
    });
  }

  override createLogisticsTask(_payload: LogisticsTaskUpsertPayload): Promise<unknown> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateLogisticsTask(_id: string, _payload: LogisticsTaskUpsertPayload): Promise<unknown> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteLogisticsTask(_id: string): Promise<void> {
    return Promise.resolve();
  }

  override getMaintenanceTypes(query: { search?: string; page?: number; limit?: number } = {}): Promise<PagedResult<MaintenanceTypeRecord>> {
    return Promise.resolve({ items: [], page: query.page ?? 1, limit: query.limit ?? 10, total: 0 });
  }

  override getMaintenanceTypeById(_id: string): Promise<MaintenanceTypeRecord | null> {
    return Promise.resolve(null);
  }

  override createMaintenanceType(_payload: { name: string }): Promise<MaintenanceTypeRecord> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override updateMaintenanceType(_id: string, _payload: { name: string }): Promise<MaintenanceTypeRecord> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteMaintenanceType(_id: string): Promise<void> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
  
  override updateBtlUser(_id: string, _payload: BtlUserUpdatePayload): Promise<BtlUserApiRecord | null> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteBtlUser(_ids: string[]): Promise<boolean> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteLogisticUser(_ids: string[]): Promise<boolean> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
   override deleteAdmin(_ids: string[]): Promise<boolean> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override deleteFuel(_ids: string[]): Promise<boolean> {
    return Promise.reject(new Error('Not implemented in mock'));
  }
  override deleteVehicle(_ids: string[]): Promise<boolean> {
    return Promise.reject(new Error('Not implemented in mock'));
  }

  override createFuelRequest(_payload: CreateFuelRequestPayload): Promise<FuelRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override async approveMaintenance(_vehicleId: string, _maintenanceRequestId: string, _payload: maintenanceActionPayload): Promise<MaintenanceRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override async actionFuel(_id: string, _payload: fuelActionPayload): Promise<FuelRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override reviewLogisticsTask(_taskId: string, _payload: LogisticsTaskReviewPayload): Promise<LogisticsTaskReviewResult | null> {
    return Promise.resolve(null);
  }

  override reviewAgentTask(_taskId: string, _payload: AgentTaskReviewPayload): Promise<AgentTaskReviewResult | null> {
    return Promise.resolve(null);
  }

  override getMaintenanceRequests(_query?: MaintenanceRequestListQuery): Promise<PagedResult<MaintenanceRequestApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override getMaintenanceRequestById(_vehicleId: string, _maintenanceRequestId: string): Promise<MaintenanceRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override deleteMaintenanceRequest(_vehicleId: string, _maintenanceRequestId: string): Promise<boolean> {
    return Promise.resolve(true);
  }
  override deleteProspect(_id: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override addProspectStockTransactions(_id: string, _payload: ProspectStockTransactionPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override updateProspectStockVariant(_id: string, _variantId: string, _payload: UpdateProspectStockVariantPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override deleteProspectStockCategory(_id: string, _categoryId: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override addPosStockTransactions(_id: string, _payload: PosStockTransactionPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override updatePosStockVariant(_id: string, _variantId: string, _payload: UpdatePosStockVariantPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override deletePosStockCategory(_id: string, _categoryId: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override addAgentStockTransaction(_payload: AgentInventoryTransactionPayload): Promise<boolean> {
    return Promise.resolve(true);
  }

  override createMaintenanceRequest(_payload: CreateMaintenanceRequestPayload): Promise<MaintenanceRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override updateMaintenanceRequest(_vehicleId: string, _maintenanceRequestId: string, _payload: UpdateMaintenanceRequestPayload): Promise<MaintenanceRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  override updateFuelRequest(_id: string, _payload: UpdateFuelRequestPayload): Promise<FuelRequestApiRecord | null> {
    return Promise.resolve(null);
  }

  
  override getVehicles(): Promise<PagedResult<VehicleApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override createVehicle(payload: CreateVehiclePayload): Promise<VehicleApiRecord> {
    return Promise.resolve({
      id: `V-MOCK-${Date.now()}`,
      registrationNumber: payload.registrationNumber,
      make: '',
      modelName: payload.modelName,
      vehicleName: payload.vehicleName,
      type: payload.type,
      fuelType: payload.fuelType,
      status: payload.status,
      isActive: payload.isActive,
      assignedToAuthUserId: payload.assignedToAuthUserId ?? null,
      currentAgencyId: payload.currentAgencyId ?? null,
      documents: payload.documents ?? []
    });
  }

  override getFuelById(_id: string): Promise<FuelRequestApiRecord | null> {
    return Promise.resolve(null);
  }
  
   override getVehicleById(_id: string): Promise<VehicleApiRecord | null> {
    return Promise.resolve(null);
  }

  override updateVehicle(_id: string, _payload: UpdateVehiclePayload): Promise<VehicleApiRecord | null> {
    return Promise.resolve(null);
  }

  override createVehicleDocument(_payload: CreateVehicleDocumentStandalonePayload): Promise<VehicleDocumentApiRecord | null> {
    return Promise.resolve(null);
  }

  override updateVehicleDocument(_documentId: string, _payload: UpdateVehicleDocumentPayload): Promise<VehicleDocumentApiRecord | null> {
    return Promise.resolve(null);
  }

  override getVehicleDocuments(_query?: VehicleDocumentListQuery): Promise<PagedResult<VehicleDocumentApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override getVehicleDocumentsByVehicleId(_vehicleId: string, _query?: { page?: number; limit?: number }): Promise<PagedResult<VehicleDocumentApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }

  override deleteVehicleDocument(_documentId: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  override getFuelRequests(_query?: FuelRequestListQuery): Promise<PagedResult<FuelRequestApiRecord>> {
    return Promise.resolve({ items: [], page: 1, limit: 20, total: 0 });
  }
}
